import { type ReactNode, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { AppRecordsTable } from '@/components/apps/AppRecordsTable';
import { AppLinkedToolsBar } from '@/components/apps/AppLinkedToolsBar';
import { useAppToolLinks } from '@/components/apps/useAppToolLinks';
import { updateAppRecord } from '@/lib/app-records';
import { type AppListQuery, EMPTY_APP_LIST_QUERY, filterAppRecords, statusCounts, toAggregatable } from './app-records-model';
import { AppListFilters, activeFilterCount } from '@/components/apps/AppListFilters';
import { MEDDIC_GRID, setAppReviewGrid } from '@/lib/app-reviews';
import { useAppData } from '@/components/apps/useAppData';
import { useAppNextActions } from '@/components/apps/useAppNextActions';
import { applyFilters } from '@shared/app-aggregates';
import type { AppFilter } from '@shared/types/app-spec';

interface AppRecordsViewProps {
  appId: string;
  appKey: string;
  canManage: boolean;
  /** Reports whether the app has a list yet, and a review grid (the tabs show only then). */
  onAvailability?: (available: boolean, title: string | null, hasReviewGrid: boolean) => void;
  /** F6: the columns chosen by the app's description. */
  visibleColumns?: string[];
  /** F6: the objects this block shows (e.g. only « À revoir »). */
  filter?: AppFilter[];
  /** What an empty list offers, e.g. launching the app. */
  emptyAction?: ReactNode;
  /** The app's tool: its objects' next actions live in its work Thread (N2). */
  flowId?: string | null;
}

/**
 * What the object drawer receives from the list: the order the person saw, for
 * « précédent / suivant », and whether the list (or Mon travail, for a séance)
 * is the previous history entry: closing then goes back instead of adding one.
 */
export interface AppObjectNavigationState {
  order?: string[];
  fromList?: boolean;
  fromWork?: boolean;
}

/** F5a: the app's accumulated list; a row opens the object in a drawer at its own address (shareable link). */
export const AppRecordsView = ({ appId, appKey, canManage, onAvailability, visibleColumns, filter, emptyAction, flowId }: AppRecordsViewProps) => {
  const navigate = useNavigate();
  // The object open in the drawer (/apps/:key/o/:recordId) stays marked in the list.
  const { recordId: openRecordId } = useParams<{ recordId: string }>();
  const data = useAppData(appId, onAvailability);
  const { list, members, userId, error, setError, labels, reload, run } = data;
  // « Ouvrir » on a batch of Aujourd'hui lands here with ?suite=1: the objects with a next action;
  // a set cited by the chat with ?q=, ?statut= and ?etiquette= (appSetHref): the list filtered the same way.
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState<AppListQuery>(() => ({
    ...EMPTY_APP_LIST_QUERY,
    search: searchParams.get('q') ?? '',
    status: searchParams.get('statut'),
    label: searchParams.get('etiquette'),
    withNextAction: Boolean(flowId) && searchParams.get('suite') === '1',
  }));
  const nextActions = useAppNextActions(flowId);
  const nextActionIds = useMemo(() => nextActions ? new Set(Object.keys(nextActions)) : null, [nextActions]);
  const toolLinks = useAppToolLinks(appId);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const scoped = useMemo(() => {
    if (!list || !filter?.length) return list?.records ?? [];
    const kept = new Set(applyFilters(list.records.map((record) => toAggregatable(record, members)), filter).map((object) => object.id));
    return list.records.filter((record) => kept.has(record.id));
  }, [list, filter, members]);
  const visible = useMemo(
    () => list ? filterAppRecords(scoped, list.collection, query, userId, labels.byRecord, nextActionIds) : [],
    [list, scoped, query, userId, labels.byRecord, nextActionIds],
  );
  const counts = useMemo(() => statusCounts(scoped, query.archived), [scoped, query.archived]);
  if (!list) return error ? <p role="alert" className="text-sm text-[var(--domino-critical)]">{error}</p> : null;
  const { collection } = list;
  const filtered = query.search.trim() !== '' || activeFilterCount(query) > 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <AppListFilters query={query} onChange={setQuery} statuses={collection.statuses} counts={counts} labels={labels.labels}
          nextActionCount={flowId ? scoped.filter((record) => record.archivedAt === null && nextActionIds?.has(record.id)).length : null} />
        {canManage && !collection.reviewGrid && collection.mode === 'runs' && (
          <button type="button" className="rounded-lg border border-domino-cardBorder bg-domino-white px-3 py-1.5 text-sm text-domino-ink transition hover:border-domino-aubergine"
            onClick={() => void setAppReviewGrid(appId, MEDDIC_GRID).then(() => reload()).catch((cause) => setError(cause instanceof Error ? cause.message : 'Grille non ajoutée.'))}>
            Ajouter la grille MEDDIC
          </button>
        )}
        <label className="relative ml-auto w-full sm:w-64">
          <span className="sr-only">Rechercher dans la liste</span>
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-domino-inkSoft" aria-hidden="true" />
          <input type="search" value={query.search} onChange={(event) => setQuery({ ...query, search: event.target.value })} placeholder="Rechercher"
            className="w-full rounded-lg border border-domino-cardBorder bg-domino-white py-1.5 pl-8 pr-2 text-sm text-domino-ink placeholder:text-domino-inkSoft focus:border-domino-aubergine focus:outline-none" />
        </label>
      </div>
      {error && <p role="alert" className="text-sm text-[var(--domino-critical)]">{error}</p>}
      {collection.lastMerge?.issue && (
        <p role="status" className="rounded-lg border border-domino-cardBorder bg-domino-muted px-3 py-2 text-sm text-domino-ink">
          {collection.lastMerge.issue === 'no_table'
            ? 'Le dernier lancement n’a pas produit le tableau de la liste : elle est restée inchangée.'
            : `Le dernier lancement a laissé de côté ${collection.lastMerge.skipped} ligne${collection.lastMerge.skipped > 1 ? 's' : ''} sans repère unique.`}
        </p>
      )}
      {selected.size > 0 && (
        <AppLinkedToolsBar links={toolLinks.links} collection={collection}
          records={list.records.filter((record) => selected.has(record.id))} onClear={() => setSelected(new Set())} />
      )}
      <AppRecordsTable collection={collection} records={visible} members={members} selectedId={openRecordId ?? null}
        selection={toolLinks.links.length > 0 ? {
          ids: selected,
          onToggle: (recordId, on) => setSelected((current) => {
            const next = new Set(current);
            if (on) next.add(recordId); else next.delete(recordId);
            return next;
          }),
          onToggleAll: (on) => setSelected(on ? new Set(visible.map((record) => record.id)) : new Set()),
        } : null}
        labels={labels.labels} labelsByRecord={labels.byRecord}
        onSelect={(record) => navigate(`/apps/${encodeURIComponent(appKey)}/o/${record.id}`, {
          state: { order: visible.map((item) => item.id), fromList: true } satisfies AppObjectNavigationState,
        })}
        onStatus={(record, status) => void run(() => updateAppRecord(record, { status }))} visibleColumns={visibleColumns}
        nextActions={flowId ? nextActions ?? {} : null}
        empty={filtered
          ? <p className="text-sm text-domino-inkSoft">Aucune ligne ne correspond.</p>
          : <div className="flex flex-col items-center gap-3"><p className="text-sm text-domino-inkSoft">Aucune ligne pour l’instant</p>{emptyAction}</div>} />
    </div>
  );
};
